'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@/i18n/routing';

import { API_ENDPOINTS } from '@/lib/api/config';
import { prefetchAuthenticatedBlob } from '@/hooks/use-authenticated-blob-url';
import { qrReviewQueries } from '../services/qr-review.queries';
import {
  useStartReviewMutation,
  useEndSessionMutation,
  useSubmitVerdictMutation
} from '../services/qr-review.mutations';
import type { QrReviewBatch, QrReviewPageRecord } from '../types';

interface UseQrReviewSessionReturn {
  batch: QrReviewBatch | undefined;
  pages: QrReviewPageRecord[];
  selectedPageIndex: number;
  setSelectedPageIndex: React.Dispatch<React.SetStateAction<number>>;
  selectedPage: QrReviewPageRecord | null;
  reviewedCount: number;
  canEndSession: boolean;
  progressPct: number;
  goToPrev: () => void;
  goToNext: () => void;
  handleManualVerdict: (verdict: 'passed' | 'rejected') => Promise<void>;
  isVerdictPending: boolean;
  handleEndSession: () => Promise<void>;
  isEndingSession: boolean;
}

export function useQrReviewSession(
  batchId: number,
  options: { readOnly?: boolean } = {}
): UseQrReviewSessionReturn {
  const { readOnly = false } = options;
  const router = useRouter();
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);

  // Queries & mutations
  const { data: batchData } = useQuery(qrReviewQueries.detail(batchId));
  const batch = batchData?.data as QrReviewBatch | undefined;

  const startReviewMutation = useStartReviewMutation();
  const endSessionMutation = useEndSessionMutation();
  const submitMutation = useSubmitVerdictMutation();

  // Auto-start review when batch is in pending_qa (skipped in read-only mode)
  useEffect(() => {
    if (readOnly) return;
    if (batch?.qa_status === 'pending_qa') {
      startReviewMutation.mutate(batchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch?.qa_status, readOnly]);

  // Derived values
  const pages: QrReviewPageRecord[] = useMemo(
    () => batch?.pages ?? [],
    [batch?.pages]
  );
  const selectedPage = pages[selectedPageIndex] ?? null;
  const reviewedCount = pages.filter((p) => p.qa_verdict !== null).length;
  const canEndSession = pages.length > 0 && reviewedCount >= pages.length;
  const progressPct =
    pages.length > 0 ? Math.round((reviewedCount / pages.length) * 100) : 0;

  // Prefetch the two pages on either side of the selected one so prev/next
  // feels instant. Sequential review is the dominant flow.
  useEffect(() => {
    if (pages.length === 0) return;
    const offsets = [-2, -1, 1, 2];
    for (const offset of offsets) {
      const i = selectedPageIndex + offset;
      if (i < 0 || i >= pages.length) continue;
      const neighbor = pages[i];
      if (!neighbor) continue;
      prefetchAuthenticatedBlob(
        API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(batchId, neighbor.id)
      );
    }
  }, [selectedPageIndex, pages, batchId]);

  // Navigation
  const goToPrev = useCallback(() => {
    setSelectedPageIndex((i) => Math.max(0, i - 1));
  }, []);

  const goToNext = useCallback(() => {
    setSelectedPageIndex((i) => Math.min(pages.length - 1, i + 1));
  }, [pages.length]);

  // Manual verdict handler with auto-advance
  const handleManualVerdict = useCallback(
    async (verdict: 'passed' | 'rejected') => {
      if (readOnly) return;
      if (!selectedPage || selectedPage.qa_verdict !== null) return;
      try {
        await submitMutation.mutateAsync({
          batchId,
          pageId: selectedPage.id,
          verdict
        });
        // Auto-advance to next unreviewed page
        const nextIdx = pages.findIndex(
          (p, i) => i > selectedPageIndex && p.qa_verdict === null
        );
        if (nextIdx !== -1) setSelectedPageIndex(nextIdx);
      } catch {
        // handled by mutation
      }
    },
    [readOnly, selectedPage, submitMutation, batchId, pages, selectedPageIndex]
  );

  // End session handler
  const handleEndSession = useCallback(async () => {
    if (readOnly) return;
    try {
      await endSessionMutation.mutateAsync(batchId);
      router.push('/dashboard/qr-review');
    } catch {
      // handled globally by query client
    }
  }, [readOnly, endSessionMutation, batchId, router]);

  return {
    batch,
    pages,
    selectedPageIndex,
    setSelectedPageIndex,
    selectedPage,
    reviewedCount,
    canEndSession: readOnly ? false : canEndSession,
    progressPct,
    goToPrev,
    goToNext,
    handleManualVerdict,
    isVerdictPending: readOnly ? false : submitMutation.isPending,
    handleEndSession,
    isEndingSession: readOnly ? false : endSessionMutation.isPending
  };
}
